// deps
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// types
import { PreSignedUrlData, PreSignedUrlJobStatus, PC_HALFCARD_STATES } from "@swiggy-private/cart-types";

// internal-deps
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { isEmpty } from "@swiggy-private/js-utils";

// apis
import { fetchJobStatus, fetchPreSignedUrl, postJobSubmit } from "@cart-client/apis/publicCart";

// helpers
import { hasUploadRejectionCta } from "@cart-client/components/toing-price-comparison-halfcard/helpers";
import { getTotalSavings } from "@cart-client/helpers/Cart";

// hooks
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { clickResultRejectD2C, clickCloseD2CHalfcard } from "@cart-client/analytics/events/ToingLpg";

const JOB_STATUS_POLL_INTERVAL_MS = 3000;
const MAX_POLL_TIME_MS = 45000;

const useToingPriceComparison = () => {
    const { clickEvent } = useContext(AnalyticsContext);
    const [isHalfCardOpen, setIsHalfCardOpen] = useState<boolean>(false);
    const [pcHalfCardState, setPcHalfCardState] = useState<PC_HALFCARD_STATES>(PC_HALFCARD_STATES.INSTRUCTIONS);
    const [presignedData, setPresignedData] = useState<PreSignedUrlData | null>(null);
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { cartState } = useContext(CartContext);
    const { localCart, reviewedCart: cartResponse } = cartState;
    const totalSavings = useMemo(() => getTotalSavings(cartResponse?.data), [cartResponse?.data]);

    const openHalfCard = useCallback(() => {
        setIsHalfCardOpen(true);
    }, []);

    const closeHalfCard = useCallback(() => {
        clickCloseD2CHalfcard(clickEvent, {
            state: String(pcHalfCardState),
            text: "",
            subText: "",
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            uploadId: presignedData?.request_id,
        });
        setIsHalfCardOpen(false);
    }, [
        clickEvent,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurantId,
        presignedData?.request_id,
        pcHalfCardState,
    ]);

    const closeHalfCardSilent = useCallback(() => {
        setIsHalfCardOpen(false);
    }, []);

    const resetHalfCard = useCallback(() => {
        setPcHalfCardState(PC_HALFCARD_STATES.INSTRUCTIONS);
        setPresignedData(null);
    }, []);

    const updatePcHalfCardState = useCallback((state: PC_HALFCARD_STATES) => {
        setPcHalfCardState(state);
    }, []);

    const priceComparisonData = useMemo(
        () => cartResponse?.data?.toing_presentational_config?.toing_price_comparison_data,
        [cartResponse?.data?.toing_presentational_config?.toing_price_comparison_data],
    );

    const isFreeCashApplicable = useMemo(() => {
        return priceComparisonData?.is_free_cash_applicable ?? false;
    }, [priceComparisonData?.is_free_cash_applicable]);

    const refreshPresignedUrl = useCallback(async () => {
        setPresignedData(null);
        try {
            const response = await fetchPreSignedUrl();
            if (response.statusCode === 0 && response.data?.presigned_url) {
                setPresignedData(response.data);
            }
        } catch {
            setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
        }
    }, []);

    useEffect(() => {
        if (!isHalfCardOpen) {
            resetHalfCard();
        }
    }, [isHalfCardOpen, resetHalfCard]);

    useEffect(() => {
        if (isHalfCardOpen) {
            refreshPresignedUrl();
        }
    }, [isHalfCardOpen, refreshPresignedUrl]);

    useEffect(() => {
        if (pcHalfCardState === PC_HALFCARD_STATES.SUCCESS && hasUploadRejectionCta(priceComparisonData)) {
            refreshPresignedUrl();
        }
    }, [pcHalfCardState, priceComparisonData, refreshPresignedUrl]);

    useEffect(() => {
        if (pcHalfCardState !== PC_HALFCARD_STATES.SUCCESS || !priceComparisonData) {
            return;
        }
        if (
            isEmpty(priceComparisonData.success_state_halfcard_data) &&
            isEmpty(priceComparisonData.rejection_state_halfcard_data)
        ) {
            setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
        }
    }, [pcHalfCardState, priceComparisonData]);

    useEffect(() => {
        if (pcHalfCardState !== PC_HALFCARD_STATES.SUCCESS || !priceComparisonData) {
            return;
        }
        if (
            isEmpty(priceComparisonData.success_state_halfcard_data) &&
            isEmpty(priceComparisonData.rejection_state_halfcard_data)
        ) {
            setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
        }
    }, [pcHalfCardState, priceComparisonData]);

    useEffect(() => {
        if (pcHalfCardState !== PC_HALFCARD_STATES.SCANNING || !presignedData?.request_id) {
            return;
        }

        const requestId = presignedData.request_id;
        const startTime = Date.now();
        let cancelled = false;

        const stopPolling = () => {
            cancelled = true;
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
        };

        const pollOnce = async () => {
            if (cancelled) return;

            try {
                const response = await fetchJobStatus(requestId);
                if (cancelled) return;

                if (response.statusCode !== 0) {
                    stopPolling();
                    setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
                    return;
                }

                if (response.jobstatus === PreSignedUrlJobStatus.COMPLETED) {
                    if (!updateCartInProgress) {
                        const updateCartResponse = await doUpdateCart({
                            ...localCart,
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            toing_req_attributes: {
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                toing_lpg_fgs_job_id: requestId,
                            },
                        });

                        const freshPriceComparisonData =
                            updateCartResponse?.data?.toing_presentational_config?.toing_price_comparison_data;

                        if (
                            !isEmpty(freshPriceComparisonData?.success_state_halfcard_data) ||
                            !isEmpty(freshPriceComparisonData?.rejection_state_halfcard_data)
                        ) {
                            setPcHalfCardState(PC_HALFCARD_STATES.SUCCESS);
                        } else {
                            setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
                        }
                    }
                    stopPolling();
                    return;
                }

                if (Date.now() - startTime >= MAX_POLL_TIME_MS) {
                    stopPolling();
                    setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
                    return;
                }

                pollTimeoutRef.current = setTimeout(pollOnce, JOB_STATUS_POLL_INTERVAL_MS);
            } catch (err) {
                if (cancelled) return;
                stopPolling();
                setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
            }
        };

        pollOnce();

        return stopPolling;
    }, [
        doUpdateCart,
        localCart,
        pcHalfCardState,
        presignedData?.request_id,
        priceComparisonData?.rejection_state_halfcard_data,
        priceComparisonData?.success_state_halfcard_data,
        updateCartInProgress,
        totalSavings,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurant_id,
    ]);

    const submitImage = useCallback(async () => {
        try {
            if (pcHalfCardState === PC_HALFCARD_STATES.SUCCESS && hasUploadRejectionCta(priceComparisonData)) {
                clickResultRejectD2C(clickEvent, {
                    text: priceComparisonData?.rejection_state_halfcard_data?.title ?? "",
                    subText: priceComparisonData?.rejection_state_halfcard_data?.subtitle ?? "",
                    cta: priceComparisonData?.rejection_state_halfcard_data?.cta?.text ?? "",
                    cartId: cartState?.localCart?.cart_id?.toString(),
                    state: priceComparisonData?.rejection_state_halfcard_data?.title
                        ? "OpponentIsCheaper"
                        : PC_HALFCARD_STATES.SUCCESS,
                    restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
                    uploadId: presignedData?.request_id,
                });
            }

            const response = await postJobSubmit(presignedData?.presigned_url ?? "", presignedData?.request_id ?? "");
            if (response.statusCode === 0) {
                setPcHalfCardState(PC_HALFCARD_STATES.SCANNING);
            } else {
                setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
            }
        } catch (err) {
            setPcHalfCardState(PC_HALFCARD_STATES.ERROR);
        }
    }, [
        pcHalfCardState,
        priceComparisonData,
        clickEvent,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurantId,
        presignedData?.request_id,
        presignedData?.presigned_url,
    ]);

    return {
        pcHalfCardState,
        updatePcHalfCardState,
        presignedData,
        submitImage,
        isHalfCardOpen,
        openHalfCard,
        closeHalfCard,
        closeHalfCardSilent,
        priceComparisonData,
        isFreeCashApplicable,
        totalSavings,
    };
};

export default useToingPriceComparison;
